import axios from "../axios";

const getAuthHeaders = () => {
  const authToken = localStorage.getItem("access-Token-Edify");
  if (!authToken) return {};
  return { headers: { Authorization: `Bearer ${authToken}` } };
};

export type ProfessionalResponse = {
  id: number | string;
  type?: "PERSON" | "COMPANY";
  name?: string;
  last_name?: string;
  razon_social?: string;
  validated?: boolean;
  username?: string;
  url_site_web?: string;
  zone?: string;
  image_profile?: string;
  phone?: string;
  skill?: Array<{ skill: { id?: number; name: string } }>;
  skills?: Array<{ id?: number; name: string }>;
  portfolio?: Array<{
    id?: number | string;
    title?: string;
    portfolio_images?: Array<{ image: string }>;
  }>;
  stars?: number;
};

export type SkillResponse = {
  id: number;
  name: string;
  users?: number;
};

export type SkillCatalogItem = {
  id: number;
  name: string;
};

export type JurisdictionResponse = {
  id: number;
  name: string;
  userCount?: number;
  allCountryCount?: number;
};

export type UserFilterRol = "ALL" | "CLIENT" | "JOBBOARD" | "MARKET";

export type FilterParams = {
  skillIds?: number[];
  jurisdictionIds?: number[];
  allCountry?: boolean;
  order?: "ASC" | "DESC";
  page?: number;
  pageSize?: number;
  type?: "PERSON" | "COMPANY";
  rol?: UserFilterRol;
  identityValidated?: boolean;
  featured?: boolean;
  name?: string;
};

export type FilterResponse = {
  users: ProfessionalResponse[];
  itemCount: number;
  page: number;
  take: number;
};

const normalizeSkillName = (name: string) =>
  name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

export const sanitizeProfessionals = (professionals: ProfessionalResponse[]) =>
  professionals.filter((professional) => {
    if (!professional.skill || !Array.isArray(professional.skill)) return true;
    return !professional.skill.some((item) => {
      const skillName = item.skill?.name;
      return skillName ? normalizeSkillName(skillName) === "otros" : false;
    });
  });

const normalizeSkills = (skills: SkillResponse[]) =>
  [...skills].sort((a, b) => a.name.localeCompare(b.name));

const normalizeJurisdictions = (jurisdictions: JurisdictionResponse[]) =>
  [...jurisdictions].sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));

const normalizeFilterResponse = (response: any): FilterResponse => {
  const users = Array.isArray(response)
    ? response
    : (response?.users ?? response?.data ?? []);
  const list = Array.isArray(users) ? users : [];
  return {
    users: list,
    itemCount: response?.itemCount ?? list.length,
    page: response?.page ?? 1,
    take: response?.take ?? 12,
  };
};

const buildQueryParams = (params: FilterParams) => {
  const searchParams = new URLSearchParams();
  params.skillIds?.forEach((id) => searchParams.append("skillIds", String(id)));
  params.jurisdictionIds?.forEach((id) => searchParams.append("jurisdictionIds", String(id)));
  if (params.allCountry) searchParams.append("allCountry", "true");
  if (params.type) searchParams.append("type", params.type);
  if (params.rol) searchParams.append("rol", params.rol);
  if (params.identityValidated) searchParams.append("identity_validated", "true");
  if (params.featured) searchParams.append("featured", "true");
  const nameTerm = params.name?.trim();
  if (nameTerm) searchParams.append("name", nameTerm);
  searchParams.append("order", params.order ?? "ASC");
  searchParams.append("page", String(params.page ?? 1));
  searchParams.append("take", String(params.pageSize ?? 12));
  return searchParams.toString();
};

export const profesionalesService = {
  fetchFeaturedUsers: async () => {
    const response = await axios.get("/user/featured", getAuthHeaders());
    const raw = response?.data;
    const list = Array.isArray(raw) ? raw : (raw?.users ?? raw?.data ?? []);
    return sanitizeProfessionals(list ?? []);
  },
  fetchSkills: async () => {
    const { data } = await axios.get<{ response: { results: SkillResponse[] } }>("/skill/AllUsers", getAuthHeaders());
    const skills = data?.response?.results ?? [];
    return normalizeSkills(skills);
  },
  fetchJurisdictions: async () => {
    const { data } = await axios.get<JurisdictionResponse[]>("/user/count/jurisdiction", getAuthHeaders());
    return normalizeJurisdictions(data ?? []);
  },
  fetchSkillCatalog: async () => {
    const { data } = await axios.get<SkillCatalogItem[]>("/skill/list", getAuthHeaders());
    return (data ?? []).map((item) => ({
      id: Number(item.id),
      name: String(item.name ?? "").trim(),
    }));
  },
  fetchFilteredProfessionals: async (params: FilterParams) => {
    const query = buildQueryParams(params);
    const { data } = await axios.get<FilterResponse>(`/user/filter?${query}`, getAuthHeaders());
    const normalized = normalizeFilterResponse(data ?? { users: [], itemCount: 0, page: 1, take: params.pageSize ?? 12 });
    return {
      users: sanitizeProfessionals(normalized.users),
      itemCount: normalized.itemCount,
      page: normalized.page,
      take: normalized.take,
    };
  },
};
